import {v4 as uuid} from 'uuid';
import {
    ColumnsNamesForMarkers,
    ColumnsNamesForMarkersType,
    distanceThreshold,
    EditingFieldsType,
    DEFAULT_PLANNED_COLUMNS,
    DEFAULT_DELI_TITLE_COLUMNS,
    DEFAULT_DELI_TOTALS_COLUMNS,
    DEFAULT_TABLE_COLUMNS,
    SELECTED_PLANNED_FIELDS_STORE_KEY,
    SELECTED_ROUTE_TITLE_FIELDS_STORE_KEY,
    SELECTED_ROUTE_TOTALS_FIELDS_STORE_KEY,
    SELECTED_ROUTE_TABLE_FIELDS_STORE_KEY,
    ADVDISTRDATA
} from './constants';
import {RemovedMarker} from './components/Map/Route/Route';
import {MarkerType, RouteType, MapSettings} from './types';
import {Column, Form, Record} from '../../stores/Form';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {PriorityIcon} from '@priority-software/priority-style-react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {
    getShowTotalsSettingsFromServer,
    getADPmapSettingsFromServer
} from './services/serverDataStore';
import {texts} from './texts';
import {ViewType} from './constants';

export const filterRecordsByType = (
    records: Record[],
    columnID: number,
    searchData: DropdownItemData[]
): Record[] => {
    if (searchData.length === 0) {
        return records.filter(r => !r.isEmpty());
    }
    const filteredRecords = records.filter(record => {
        if (record.isEmpty()) return false;

        const field = record?.getField(columnID);
        return searchData.some(
            item =>
                item.value === field?.currentValue ||
                (item.value === '' && field?.currentValue !== 'O')
        );
    });

    return filteredRecords;
};

export const setVisibleColumns = (form: Form, visibleColName: string[] = []): Column[] => {
    if (!form) {
        return [];
    }
    const newColumns = visibleColName?.map(name => form.columns.find(c => c.Name === name));
    return newColumns?.filter(Boolean);
};

export const getABBRlabel = (record: Record, columnID: number): string => {
    const userDescription = record?.getField(columnID)?.currentValue as string;

    let label = '';
    if (userDescription) {
        const description = userDescription.split(' ');
        label = (description[0][0] + (description?.[1]?.[0] || '')).toUpperCase();
    }
    return label;
};

export const getColumnIdFromName = (columns: Column[], name: string): number => {
    return columns.find(column => column.Name === name)?.id;
};

export const mappedColumnIds = (columns: Column[]) => {
    if (!columns) return {};
    return columns.reduce((acc: {[key: string]: number}, column) => {
        if (ColumnsNamesForMarkersType.includes(column.Name)) {
            acc[column.Name] = column.id;
        }

        return acc;
    }, {});
};

export const addressesColumns = [
    ColumnsNamesForMarkers.ADDRESS,
    ColumnsNamesForMarkers.ADDRESS2,
    ColumnsNamesForMarkers.STATE,
    ColumnsNamesForMarkers.COUNTRYNAME
];

export const buildAddress = (record: Record, columnsIDs: {[key: string]: number}) => {
    const address = [];
    addressesColumns.forEach(columnName => {
        const value = record?.getField(columnsIDs[columnName])?.currentValue;
        if (value) {
            address.push(value);
        }
    });
    return address.join(' ');
};

export const prepareRowsToMarkersType = (
    records: Record[],
    columnsIDs: {[key: string]: number},
    route?: RouteType
): MarkerType[] => {
    return records
        .map((r, idx) => {
            const label =
                r.parentForm.ename === ADVDISTRDATA
                    ? getABBRlabel(r, columnsIDs[ColumnsNamesForMarkers.CUSTDES])
                    : String(idx + 1);

            return {
                id: uuid(),
                address: buildAddress(r, columnsIDs),
                label,
                selected: false,
                lat: Number(r?.getField(columnsIDs[ColumnsNamesForMarkers.LATITUDE])?.currentValue),
                lng: Number(
                    r?.getField(columnsIDs[ColumnsNamesForMarkers.LONGITUDE])?.currentValue
                ),
                color: route?.color,
                route: route,
                record: r
            };
        })
        .filter(m => m.lat && m.lng);
};

export const eventHandlerMap = (
    e: google.maps.MapMouseEvent,
    map: google.maps.Map,
    updateRoute: (event: google.maps.MapMouseEvent, pointId: string, routeId: string) => void,
    removeRoute: (removedChange: RemovedMarker) => void,
    routeId: string,
    pointId: string
) => {
    const newPosition = e.latLng;
    const borders = map?.getBounds();
    if (borders.contains(newPosition)) {
        updateRoute && updateRoute(e, pointId, routeId);
    } else {
        removeRoute &&
            removeRoute({
                routeId,
                pointId
            });
    }
};

export const calculatedDistance = (
    geometryLibrary: google.maps.GeometryLibrary,
    zoomFactor: number,
    target: google.maps.LatLng | google.maps.LatLngLiteral,
    markerLatLang: google.maps.LatLng | google.maps.LatLngLiteral
) => {
    return (
        geometryLibrary.spherical.computeDistanceBetween(target, markerLatLang) <
        distanceThreshold * zoomFactor
    );
};

export const zoomFactor = (zoom: number) => {
    return Math.pow(2, 16 - zoom);
};

export type RouteUpdaterProps = {
    routes: RouteType[];
    zoom: number;
    event: google.maps.MapMouseEvent;
    geometryLibrary: google.maps.GeometryLibrary;
};

export const routeUpdater = ({routes, zoom, event, geometryLibrary}: RouteUpdaterProps) => {
    const zoomFactorCalculated = zoomFactor(zoom);

    routes.forEach(route => {
        route.markers.forEach(marker => {
            calculatedDistance(geometryLibrary, zoomFactorCalculated, event.latLng, marker);
        });
    });
};

export const getKeyAndDefaultColsByType = (type: EditingFieldsType) => {
    switch (type) {
        case EditingFieldsType.RouteTitle:
            return {
                storeKey: SELECTED_ROUTE_TITLE_FIELDS_STORE_KEY,
                defaultCols: DEFAULT_DELI_TITLE_COLUMNS
            };
        case EditingFieldsType.Planned:
            return {
                storeKey: SELECTED_PLANNED_FIELDS_STORE_KEY,
                defaultCols: DEFAULT_PLANNED_COLUMNS
            };
        case EditingFieldsType.RouteTileTotals:
            return {
                storeKey: SELECTED_ROUTE_TOTALS_FIELDS_STORE_KEY,
                defaultCols: DEFAULT_DELI_TOTALS_COLUMNS
            };
        case EditingFieldsType.RouteTable:
            return {
                storeKey: SELECTED_ROUTE_TABLE_FIELDS_STORE_KEY,
                defaultCols: DEFAULT_TABLE_COLUMNS
            };
        default:
            return {
                storeKey: '',
                defaultCols: []
            };
    }
};

export const getSelectedEntityCols = (type: EditingFieldsType, formCols: Column[]) => {
    if (!formCols?.length) {
        return [];
    } else if (type === EditingFieldsType.RouteTileTotals) {
        const intRealTypes = [
            priority.netitems.form.ColumnType.Real,
            priority.netitems.form.ColumnType.Int
        ];
        const res = formCols.filter(col => intRealTypes.includes(col.Type));
        return res;
    } else {
        return formCols;
    }
};

export const getRecordData = (record: Record, colName: string) => {
    if (!colName) {
        return '';
    }
    const {getField, parentForm} = record;
    return getField(parentForm.getColumnByName(colName)?.id).currentValue?.toString();
};

export const getShowTotalsForAll = async () => {
    const serverData = await getShowTotalsSettingsFromServer();
    return serverData?.showTotalsForAll;
};

export const getADPmapSettings = async (): Promise<MapSettings> => {
    const mapSettingsInServer = await getADPmapSettingsFromServer();
    return mapSettingsInServer?.mapSettings;
};

export const getViewTypeOptions = (): DropdownItemData[] => {
    return [
        {
            label: texts.planned_routes_map,
            value: ViewType.ALL,
            icon: PriorityIcon.thirdsColumns
        },
        {
            label: texts.only_map,
            value: ViewType.MAP,
            icon: PriorityIcon.map
        },

        //  need waiting for Iris sugestion about design. we have duplicate functionality
        // {
        //     label: texts.enlarged_map,
        //     value: ViewType.BIG_MAP,
        //     icon: BigMap
        // },
        {
            label: texts.routes_map,
            value: ViewType.ROUTES_MAP,
            icon: PriorityIcon.shippingMap
        },
        {
            label: texts.planned_map,
            value: ViewType.PLANNED_MAP,
            icon: PriorityIcon.plannedMap
        },
        {
            label: texts.only_routes,
            value: ViewType.ONLY_ROUTES,
            icon: PriorityIcon.localShipping
        },
        {
            label: texts.only_planned,
            value: ViewType.ONLY_PLANNED,
            icon: PriorityIcon.clipboard
        }
    ];
};
